/**
 * Generated by orval v7.17.0 🍺
 * Do not edit manually.
 * Staffing API
 * OpenAPI spec version: 1.0.0
 */
import type {
  BulkInviteExecutionResponse,
  BulkInviteUsersPayload,
  BulkInviteValidationResponse,
  DeleteUserPayload,
  GetUserInfoResponse,
  GetUserPayload,
  InviteUserPayload,
  InviteUserResponse,
  MessageResponse,
  OrgAdminsResponse,
  ReactivateUserResponse,
  ResendInvitePayload,
  RolesListResponse,
  UpdateUserPayload,
  UpdateUserResponse,
  UserListResponse,
  UserStatusChangePayload,
  UsersByOrgResponse,
} from '../types';

import { axiosInstance } from '../../axiosConfig';

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

export const getUserManagement = () => {
  /**
   * @summary Get User Info
   */
  const userManagementGetUserInfo = (
    getUserPayload: GetUserPayload,
    options?: SecondParameter<typeof axiosInstance<GetUserInfoResponse>>
  ) => {
    return axiosInstance<GetUserInfoResponse>(
      {
        url: `/get_user_info`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: getUserPayload,
      },
      options
    );
  };
  /**
   * Endpoint to get users by org_id, returning only display fields.
   * @summary Get Users By Org
   */
  const userManagementGetUsersByOrg = (
    orgId: string,
    options?: SecondParameter<typeof axiosInstance<UsersByOrgResponse>>
  ) => {
    return axiosInstance<UsersByOrgResponse>(
      { url: `/org/${orgId}/users`, method: 'GET' },
      options
    );
  };
  /**
   * Return ACTIVE org admins (display fields only) for any member of the org.
   * @summary Get Org Admins
   */
  const userManagementGetOrgAdmins = (
    orgId: string,
    options?: SecondParameter<typeof axiosInstance<OrgAdminsResponse>>
  ) => {
    return axiosInstance<OrgAdminsResponse>(
      { url: `/org/${orgId}/admins`, method: 'GET' },
      options
    );
  };
  /**
 * Get all users for the organization.

Returns user list with their roles from the database.
 * @summary List Users
 */
  const userManagementListUsers = (
    orgId: string,
    options?: SecondParameter<typeof axiosInstance<UserListResponse>>
  ) => {
    return axiosInstance<UserListResponse>(
      { url: `/admin/org/${orgId}/users/list`, method: 'POST' },
      options
    );
  };
  /**
 * Get available roles for the organization.

Returns list of roles that can be assigned to users.
 * @summary Get Roles
 */
  const userManagementGetRoles = (
    orgId: string,
    options?: SecondParameter<typeof axiosInstance<RolesListResponse>>
  ) => {
    return axiosInstance<RolesListResponse>(
      { url: `/admin/org/${orgId}/users/roles`, method: 'POST' },
      options
    );
  };
  /**
 * Invite a new user to the organization.

Sends Stytch invitation email. User will be created in our DB via stytch webhook,
and the role will be assigned after user creation.
 * @summary Invite User
 */
  const userManagementInviteUser = (
    orgId: string,
    inviteUserPayload: InviteUserPayload,
    options?: SecondParameter<typeof axiosInstance<InviteUserResponse>>
  ) => {
    return axiosInstance<InviteUserResponse>(
      {
        url: `/admin/org/${orgId}/users/invite`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: inviteUserPayload,
      },
      options
    );
  };
  /**
 * Validate a batch of user invites before any invitations are sent.

Roles must exactly match the org's current role names.
 * @summary Validate Bulk Invite
 */
  const userManagementValidateBulkInvite = (
    orgId: string,
    bulkInviteUsersPayload: BulkInviteUsersPayload,
    options?: SecondParameter<
      typeof axiosInstance<BulkInviteValidationResponse>
    >
  ) => {
    return axiosInstance<BulkInviteValidationResponse>(
      {
        url: `/admin/org/${orgId}/users/bulk-invite/validate`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: bulkInviteUsersPayload,
      },
      options
    );
  };
  /**
   * Send a batch of user invitations after server-side validation succeeds.
   * @summary Bulk Invite Users
   */
  const userManagementBulkInviteUsers = (
    orgId: string,
    bulkInviteUsersPayload: BulkInviteUsersPayload,
    options?: SecondParameter<typeof axiosInstance<BulkInviteExecutionResponse>>
  ) => {
    return axiosInstance<BulkInviteExecutionResponse>(
      {
        url: `/admin/org/${orgId}/users/bulk-invite`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: bulkInviteUsersPayload,
      },
      options
    );
  };
  /**
 * Update user information.

Updates name in Stytch (for display) and role in the database. Omitting `roleId` keeps the
current role; an explicit `null` sets Limited Access.
 * @summary Update User
 */
  const userManagementUpdateUser = (
    orgId: string,
    updateUserPayload: UpdateUserPayload,
    options?: SecondParameter<typeof axiosInstance<UpdateUserResponse>>
  ) => {
    return axiosInstance<UpdateUserResponse>(
      {
        url: `/admin/org/${orgId}/users/update`,
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        data: updateUserPayload,
      },
      options
    );
  };
  /**
 * Delete a user from the organization.

Deletes user from Stytch and marks as deleted in local database.
 * @summary Delete User
 */
  const userManagementDeleteUser = (
    orgId: string,
    deleteUserPayload: DeleteUserPayload,
    options?: SecondParameter<typeof axiosInstance<MessageResponse>>
  ) => {
    return axiosInstance<MessageResponse>(
      {
        url: `/admin/org/${orgId}/users/delete`,
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        data: deleteUserPayload,
      },
      options
    );
  };
  /**
 * Lock a user out while keeping their record.

The next request from that user is refused and the login page shows the
deactivated card; nothing they own is deleted or reassigned.
 * @summary Deactivate User
 */
  const userManagementDeactivateUser = (
    orgId: string,
    userStatusChangePayload: UserStatusChangePayload,
    options?: SecondParameter<typeof axiosInstance<MessageResponse>>
  ) => {
    return axiosInstance<MessageResponse>(
      {
        url: `/admin/org/${orgId}/users/deactivate`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: userStatusChangePayload,
      },
      options
    );
  };
  /**
 * Restore a deactivated user with the role they had before.

A user whose sign-in account is gone is invited again instead, and becomes active once
they accept.
 * @summary Reactivate User
 */
  const userManagementReactivateUser = (
    orgId: string,
    userStatusChangePayload: UserStatusChangePayload,
    options?: SecondParameter<typeof axiosInstance<ReactivateUserResponse>>
  ) => {
    return axiosInstance<ReactivateUserResponse>(
      {
        url: `/admin/org/${orgId}/users/reactivate`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: userStatusChangePayload,
      },
      options
    );
  };
  /**
 * Resend invitation email to a pending user.

Only works for users in INVITED status.
 * @summary Resend Invite
 */
  const userManagementResendInvite = (
    orgId: string,
    resendInvitePayload: ResendInvitePayload,
    options?: SecondParameter<typeof axiosInstance<MessageResponse>>
  ) => {
    return axiosInstance<MessageResponse>(
      {
        url: `/admin/org/${orgId}/users/resend-invite`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: resendInvitePayload,
      },
      options
    );
  };
  return {
    userManagementGetUserInfo,
    userManagementGetUsersByOrg,
    userManagementGetOrgAdmins,
    userManagementListUsers,
    userManagementGetRoles,
    userManagementInviteUser,
    userManagementValidateBulkInvite,
    userManagementBulkInviteUsers,
    userManagementUpdateUser,
    userManagementDeleteUser,
    userManagementDeactivateUser,
    userManagementReactivateUser,
    userManagementResendInvite,
  };
};
export type UserManagementGetUserInfoResult = NonNullable<
  Awaited<
    ReturnType<
      ReturnType<typeof getUserManagement>['userManagementGetUserInfo']
    >
  >
>;
export type UserManagementGetUsersByOrgResult = NonNullable<
  Awaited<
    ReturnType<
      ReturnType<typeof getUserManagement>['userManagementGetUsersByOrg']
    >
  >
>;
export type UserManagementGetOrgAdminsResult = NonNullable<
  Awaited<
    ReturnType<
      ReturnType<typeof getUserManagement>['userManagementGetOrgAdmins']
    >
  >
>;
export type UserManagementListUsersResult = NonNullable<
  Awaited<
    ReturnType<ReturnType<typeof getUserManagement>['userManagementListUsers']>
  >
>;
export type UserManagementGetRolesResult = NonNullable<
  Awaited<
    ReturnType<ReturnType<typeof getUserManagement>['userManagementGetRoles']>
  >
>;
export type UserManagementInviteUserResult = NonNullable<
  Awaited<
    ReturnType<ReturnType<typeof getUserManagement>['userManagementInviteUser']>
  >
>;
export type UserManagementValidateBulkInviteResult = NonNullable<
  Awaited<
    ReturnType<
      ReturnType<typeof getUserManagement>['userManagementValidateBulkInvite']
    >
  >
>;
export type UserManagementBulkInviteUsersResult = NonNullable<
  Awaited<
    ReturnType<
      ReturnType<typeof getUserManagement>['userManagementBulkInviteUsers']
    >
  >
>;
export type UserManagementUpdateUserResult = NonNullable<
  Awaited<
    ReturnType<ReturnType<typeof getUserManagement>['userManagementUpdateUser']>
  >
>;
export type UserManagementDeleteUserResult = NonNullable<
  Awaited<
    ReturnType<ReturnType<typeof getUserManagement>['userManagementDeleteUser']>
  >
>;
export type UserManagementDeactivateUserResult = NonNullable<
  Awaited<
    ReturnType<
      ReturnType<typeof getUserManagement>['userManagementDeactivateUser']
    >
  >
>;
export type UserManagementReactivateUserResult = NonNullable<
  Awaited<
    ReturnType<
      ReturnType<typeof getUserManagement>['userManagementReactivateUser']
    >
  >
>;
export type UserManagementResendInviteResult = NonNullable<
  Awaited<
    ReturnType<
      ReturnType<typeof getUserManagement>['userManagementResendInvite']
    >
  >
>;
